"use client";

import { useEffect, useState } from "react";
import { ArrowUpRight, TrendingUp } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stats = [{
  label: "STUDENTS MENTORED",
  value: 1240,
  unit: "scholars",
  trend: "+14.2%",
  formatted: "1,240",
  hero: false
}, {
  label: "AVERAGE SCORE GAIN",
  value: 280,
  unit: "SAT points",
  trend: "+12.5%",
  formatted: "280",
  hero: true
}, {
  label: "STEM MASTERY",
  value: 98,
  unit: "% pass rate",
  trend: "+4.8%",
  formatted: "98",
  hero: false
}, {
  label: "HOURS OF TUTORING",
  value: 8500,
  unit: "total hours",
  trend: "+22.1%",
  formatted: "8,500",
  hero: false
}];
const chartData = [{
  month: "Jan",
  improvement: 120
}, {
  month: "Feb",
  improvement: 150
}, {
  month: "Mar",
  improvement: 210
}, {
  month: "Apr",
  improvement: 320
}, {
  month: "May",
  improvement: 440
}, {
  month: "Jun",
  improvement: 510
}, {
  month: "Jul",
  improvement: 630
}, {
  month: "Aug",
  improvement: 720
}, {
  month: "Sep",
  improvement: 680
}, {
  month: "Oct",
  improvement: 540
}, {
  month: "Nov",
  improvement: 390
}, {
  month: "Dec",
  improvement: 260
}];
const chartConfig = {
  improvement: {
    label: "Improvement",
    color: "var(--chart-1)"
  }
} satisfies ChartConfig;
function CountUp({
  end,
  formatted,
  start
}: {
  end: number;
  formatted: string;
  start: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    const duration = 2000;
    const startTime = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(end * eased));
      if (progress < 1) requestAnimationFrame(tick);else setCount(end);
    };
    requestAnimationFrame(tick);
  }, [start, end]);
  if (count >= end) return <>{formatted}</>;
  return <>{count.toLocaleString()}</>;
}
export default function ProvenScoreDeltas() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="802" id="proven-score-deltas" className="bg-background text-foreground relative overflow-hidden py-24 md:py-40">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='100' viewBox='0 0 56 100'%3E%3Cpath fill='none' stroke='%232563eb' stroke-width='1.2' d='M28 0L56 16.18V49.45L28 65.64L0 49.45V16.18L28 0zM28 49.45L56 65.64v33.27L28 115.09L0 98.91V65.64L28 49.45z'/%3E%3C/svg%3E\")",
      backgroundSize: "56px 100px"
    }} aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute top-1/4 -left-10 opacity-20" aria-hidden="true" style={{
      animation: "bee-drift 28s linear infinite"
    }}>
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <rect x="8" y="10" width="16" height="12" rx="2" fill="var(--chart-1)" />
          <path d="M12 10 L12 6 M20 10 L20 6" stroke="var(--chart-1)" strokeWidth="2" />
          <path d="M8 16 L4 16 M24 16 L28 16" stroke="var(--chart-1)" strokeWidth="2" />
        </svg>
      </div>

      <style>{`
        @keyframes bee-drift {
          0% { transform: translateX(0) translateY(0); }
          25% { transform: translateX(40vw) translateY(-30px); }
          50% { transform: translateX(80vw) translateY(20px); }
          75% { transform: translateX(60vw) translateY(60px); }
          100% { transform: translateX(110vw) translateY(0); }
        }
      `}</style>

      <div ref={ref} className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="font-mono text-xs tracking-[0.25em] uppercase mb-8 text-secondary">
                Academic Performance Data
              </p>

              <h2 className="font-sans font-bold text-5xl md:text-6xl lg:text-[4rem] leading-[1.05] tracking-tighter text-foreground">
                Precision metrics for{" "}
                <span className="italic text-secondary">
                  mastery
                </span>
              </h2>

              <p className="mt-8 text-lg leading-relaxed text-muted-foreground max-w-md">
                We track every variable of student progress. From SAT score improvements to complex STEM concept retention, our data-driven approach ensures measurable academic ascent.
              </p>

              {}
              <div className="mt-12 rounded-lg border border-border bg-accent/50 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                      Growth Velocity
                    </p>
                    <p className="font-sans font-semibold text-xl text-foreground mt-1">Annual Trend</p>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs font-mono text-chart-3">
                    <TrendingUp className="size-3.5" />
                    <span>+28.4%</span>
                  </div>
                </div>

                <ChartContainer config={chartConfig} className="h-32 w-full">
                  <AreaChart data={chartData} margin={{
                  top: 5,
                  right: 0,
                  left: 0,
                  bottom: 0
                }}>
                    <defs>
                      <linearGradient id="pollinationGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" hide />
                    <YAxis hide />
                    <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                    <Area type="monotone" dataKey="improvement" stroke="var(--chart-1)" strokeWidth={2} fill="url(#pollinationGradient)" />
                  </AreaChart>
                </ChartContainer>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {stats.map((stat, i) => <div key={stat.label} data-index={i} className={`group relative overflow-hidden rounded-lg border p-6 md:p-8 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${stat.hero ? "border-secondary bg-primary text-primary-foreground" : "bg-card border-border hover:border-secondary"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {}
                  {stat.hero && <div className="pointer-events-none absolute -top-6 -right-6 opacity-10" aria-hidden="true">
                      <svg width="120" height="120" viewBox="0 0 100 100" fill="none">
                        <path d="M50 5 L90 27.5 L90 72.5 L50 95 L10 72.5 L10 27.5 Z" stroke="currentColor" strokeWidth="1" fill="none" />
                      </svg>
                    </div>}

                  <div className="relative flex flex-col h-full">
                    <p className="font-mono text-[10px] md:text-xs tracking-[0.22em] uppercase" style={{
                  color: stat.hero ? "hsl(217 91% 80%)" : "var(--muted-foreground)"
                }}>
                      {stat.label}
                    </p>

                    <div className="mt-6 md:mt-8 flex-1">
                      <p className="font-sans font-bold text-4xl md:text-5xl lg:text-6xl leading-none tracking-tighter tabular-nums">
                        <CountUp end={stat.value} formatted={stat.formatted} start={isVisible} />
                      </p>
                      <p className="mt-2 text-sm opacity-80">{stat.unit}</p>
                    </div>

                    <div className="mt-6 flex items-center gap-1.5">
                      <ArrowUpRight className="size-3.5 text-chart-3" />
                      <span className="font-mono text-xs text-chart-3">
                        {stat.trend}
                      </span>
                      <span className="font-mono text-[10px] opacity-60 tracking-wider uppercase ml-1">
                        vs last year
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 flex justify-end">
              <Button asChild variant="link" className="font-mono text-xs tracking-[0.18em] uppercase p-0 h-auto text-primary hover:text-secondary">
                <Link to="/ResourcesPage">
                  Download diagnostic test
                  <ArrowUpRight className="size-3.5 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}